// src/features/support/hooks/useSupportTickets.ts
import { useEffect, useState } from 'react';

import { fetchSupportTickets } from '../api/support.api';
import type { SupportTicketDTO } from '../api/support.api';
import type { Ticket, TicketStatus } from '../components/TicketRow';

function normalizeStatus(status: unknown): TicketStatus {
  if (status === 'open' || status === 'pending' || status === 'closed') {
    return status;
  }

  // Backend hasn't confirmed a status field yet — default to 'open'
  // rather than inventing a derived value.
  return 'open';
}

function mapTicket(dto: SupportTicketDTO): Ticket {
  return {
    id: String(dto.id),
    title: dto.title ?? dto.subject ?? '-',
    ...(dto.category ? { category: dto.category } : {}),
    createdAt: dto.created_at ?? '-',
    status: normalizeStatus(dto.status),
  };
}

export interface UseSupportTicketsResult {
  tickets: Ticket[];
  loading: boolean;
  error: string | null;
}

/**
 * Data-access hook for the Support feature. Fetches
 * GET /api/support/tickets through apiClient and maps the response
 * onto the existing `Ticket` shape used by TicketList/TicketRow.
 */
export function useSupportTickets(): UseSupportTicketsResult {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchSupportTickets();

        if (!cancelled) {
          setTickets(response.tickets.map(mapTicket));
        }
      } catch {
        if (!cancelled) {
          setTickets([]);
          setError('خطا در دریافت تیکت‌های پشتیبانی.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { tickets, loading, error };
}
